import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter';
import { useOptionsDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { Spinner, QuestionModal } from '../../../../common/index.jsx';
import dayjs from 'dayjs';
import {
  Tag,
  Flex,
  Radio,
  Select,
  Space,
  Modal,
  Divider,
  Checkbox,
  Progress,
  Alert,
  Dropdown,
  Button,
  Badge,
  Grid,
} from 'antd';
import {
  AppstoreAddOutlined,
  CloseOutlined,
  InteractionOutlined,
  InboxOutlined,
  ExportOutlined,
  DeleteOutlined,
  FastForwardOutlined,
  EllipsisOutlined,
  SnippetsOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { CollapseBlock } from '../../../../common';
import { IconButton } from '../../../../common/controls/Controls';
import ApplicationTableSettings from './ApplicationTableSettings.jsx';
import { useSelector } from 'react-redux';
import useIsUserAdmin from '../../../../../hooks/useIsUserAdmin';
import useIsUserErasmusKoordinator from '../../../../../hooks/useIsUserErasmusKoordinator';
import checkPermission from '../../../../../utils/helpers/checkPermission';
import {
  useGenerateErasmusPDFMutation,
  useLazyGetErasmusPrivateFileQuery,
} from '../../../../../redux/services/erasmusApplications';
import {
  createBulkExportRuntime,
  createBulkExportTasks,
  executeBulkExportTask,
  processBulkExportQueue as runBulkExportQueue,
} from './erasmusBulkExport.js';

// --- helper na čítanie vnorených hodnôt podľa bodkovej cesty ---
const getByPath = (obj, path) =>
  path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const APPLICATION_STATUS_OPTIONS = [
  { value: 'Rozpracovaná', label: 'Rozpracovaná' },
  { value: 'Odoslaná', label: 'Odoslaná' },
  { value: 'Zrušená', label: 'Zrušená' },
  { value: 'Akceptovaná', label: 'Akceptovaná' },
  { value: 'Zamietnutá', label: 'Zamietnutá' },
];

const getApplicationStatusColor = (status) => {
  switch (status) {
    case 'Akceptovaná':
      return 'green';
    case 'Zrušená':
      return 'orange';
    case 'Zamietnutá':
      return 'red';
    case 'Odoslaná':
      return 'yellow';
    default:
      return 'blue';
  }
};

const ErasmusApplicationsTable = ({
  erasmusApplications,
  onDelete,
  onDetailClick,
  onPreviewClick,
  onCancelClick,
  onSubmitClick,
  onChangeStateClick,
  onPdfClick,
  onTimelineClick,
  onArchiveClick,
  secondaryCondition,
  disableExpandRow,
  customExpandedRowRender,
  uniqueTableId,
  selectedOption,
  setSelectedOption,
  selectedChallenge,
  setSelectedChallenge,
  challengeOptions,
  pdfGeneratingId,
  expandedRowKeys,
  onExpandedRowsChange,
  onChangeChallengeClick,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();

  // Filter hooky – používame bodkový kľúč (zladené s DataGrid TABLE_STATES_CACHE)
  const LastNameFilterProps = useDataGridTextFilter('person.last_name');
  const FirstNameFilterProps = useDataGridTextFilter('person.first_name');
  const createdDateRangeFilterProps = useDataGridDateRangeFilter('created_at');
  const UniversityFilterProps = useDataGridTextFilter(
    'choosed_university.name',
  );
  const applicationStatusFilterOptions = useMemo(
    () =>
      APPLICATION_STATUS_OPTIONS.reduce((statusMap, statusOption) => {
        statusMap[statusOption.value] = t(statusOption.label);

        return statusMap;
      }, {}),
    [t],
  );
  const StatusFilterProps = useOptionsDataGridFilter(
    'applicationStatus',
    applicationStatusFilterOptions,
  );

  const [rowToBeSubmitted, setRowToBeSubmitted] = useState(null);
  const [rowToBeCanceled, setRowToBeCanceled] = useState(null);
  const [rowToBeArchived, setRowToBeArchived] = useState(null);
  const [rowToChangeState, setRowToChangeState] = useState(null);
  const [changeStateValue, setChangeStateValue] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkChangeStateModalVisible, setBulkChangeStateModalVisible] =
    useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState(null);
  const [bulkStatusById, setBulkStatusById] = useState({});
  const [bulkChallengeModalVisible, setBulkChallengeModalVisible] =
    useState(false);
  const [bulkChallengeValue, setBulkChallengeValue] = useState(null);
  const [bulkChallengeById, setBulkChallengeById] = useState({});
  const [bulkArchiveModalVisible, setBulkArchiveModalVisible] = useState(false);
  const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);
  const [bulkExportModalVisible, setBulkExportModalVisible] = useState(false);
  const [bulkExportOptions, setBulkExportOptions] = useState({
    includeCv: true,
    includeMotivationLetter: true,
    includePhoto: true,
    includeLanguageCertificate: true,
    includePdf: true,
  });
  const [isBulkExporting, setIsBulkExporting] = useState(false);
  const [isBulkExportPaused, setIsBulkExportPaused] = useState(false);
  const [bulkExportProgress, setBulkExportProgress] = useState({
    completed: 0,
    total: 0,
    current: '',
    errors: [],
  });
  const bulkExportRuntimeRef = useRef(createBulkExportRuntime());

  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserAdmin();
  const isErasmusKoordinator = useIsUserErasmusKoordinator();
  const hasErasmusElevatedAccess = isAdmin || isErasmusKoordinator;
  const [triggerPrivateFileDownload] = useLazyGetErasmusPrivateFileQuery();
  const [generateApplicationPDF] = useGenerateErasmusPDFMutation();

  const permsForRow = {
    canSubmit: checkPermission(user, 'erasmus-applications.submit', isAdmin),
    canCancel: checkPermission(user, 'erasmus-applications.cancel', isAdmin),
    canChangeState: checkPermission(
      user,
      'erasmus-applications.changeState',
      isAdmin,
    ),
    canPdf: checkPermission(user, 'erasmus-applications.pdf', isAdmin),
    canTimeline: checkPermission(
      user,
      'erasmus-applications.timeline',
      isAdmin,
    ),
    canArchive: checkPermission(user, 'erasmus-applications.archive', isAdmin),
  };

  const getPersonDisplayName = (application) => {
    const firstName = application?.person?.first_name;
    const lastName = application?.person?.last_name;

    if (firstName || lastName) {
      return [lastName, firstName].filter(Boolean).join(' ');
    }

    return t('Neznáme');
  };

  const handleRadioChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedRowKeys([]);
        setBulkChangeStateModalVisible(false);
        setBulkChallengeModalVisible(false);
        setBulkArchiveModalVisible(false);
        setBulkDeleteModalVisible(false);
        setBulkExportModalVisible(false);
        setIsBulkExportPaused(false);
      }
      return next;
    });
  };

  const resetBulkStatusState = () => {
    setBulkChangeStateModalVisible(false);
    setBulkStatusValue(null);
    setBulkStatusById({});
  };

  const resetBulkChallengeState = () => {
    setBulkChallengeModalVisible(false);
    setBulkChallengeValue(null);
    setBulkChallengeById({});
  };

  const resetBulkExportProgress = () => {
    setBulkExportProgress({
      completed: 0,
      total: 0,
      current: '',
      errors: [],
    });
  };

  const resetBulkExportRuntime = () => {
    bulkExportRuntimeRef.current = createBulkExportRuntime();
    setIsBulkExportPaused(false);
  };

  const filteredApplications = useMemo(() => {
    if (!erasmusApplications) return [];
    let filtered = erasmusApplications;

    // Filter by archived/active status
    if (selectedOption === 'archived') {
      filtered = filtered.filter((a) => a.archived_at);
    } else {
      filtered = filtered.filter((a) => !a.archived_at);
    }

    // Filter by challenge
    if (selectedChallenge && selectedChallenge !== '') {
      filtered = filtered.filter((a) => a.challenge_id === selectedChallenge);
    }

    return filtered;
  }, [erasmusApplications, selectedOption, selectedChallenge]);

  const columns = useMemo(
    () => [
      {
        key: 'last_name',
        title: t('Priezvisko'),
        dataIndex: ['person', 'last_name'],
        width: 150,
        render: (value) => value || t('Neznáme'),
        ellipsis: true,
        sorter: dataGridTextSorter('person.last_name'),
        ...LastNameFilterProps,
        onFilter: (value, record) => {
          const v = String(value ?? '').toLowerCase();
          const text = String(
            getByPath(record, 'person.last_name') ?? '',
          ).toLowerCase();
          return text.includes(v);
        },
      },
      {
        key: 'first_name',
        title: t('Meno'),
        dataIndex: ['person', 'first_name'],
        width: 140,
        render: (value) => value || t('Neznáme'),
        ellipsis: true,
        sorter: dataGridTextSorter('person.first_name'),
        ...FirstNameFilterProps,
        onFilter: (value, record) => {
          const v = String(value ?? '').toLowerCase();
          const text = String(
            getByPath(record, 'person.first_name') ?? '',
          ).toLowerCase();
          return text.includes(v);
        },
      },
      {
        key: 'university',
        title: t('Univerzita'),
        dataIndex: ['choosed_university', 'name'],
        width: 420,
        render: (_, record) => record.choosed_university?.name || t('Neznáme'),
        ellipsis: true,
        sorter: dataGridTextSorter('choosed_university.name'),
        defaultSortOrder: 'ascend',
        ...UniversityFilterProps,
        onFilter: (value, record) => {
          const v = String(value ?? '').toLowerCase();
          const uni = String(
            getByPath(record, 'choosed_university.name') ?? '',
          ).toLowerCase();
          return uni.includes(v);
        },
      },
      {
        key: 'created',
        title: t('Vytvorené'),
        dataIndex: 'created_at',
        width: 120,
        sorter: dataGridDateSorter('created_at'),
        render: (value) => (value ? dayjs(value).format('D.M.YYYY') : ''),
        defaultSortOrder: 'ascend',
        ...createdDateRangeFilterProps,
      },
      {
        key: 'status',
        title: t('Stav'),
        dataIndex: 'applicationStatus',
        ellipsis: true,
        sorter: dataGridTextSorter('applicationStatus'),
        render: (status) => (
          <Tag color={getApplicationStatusColor(status)}>
            {String(status || '').toUpperCase()}
          </Tag>
        ),
        ...StatusFilterProps,
      },
      // Príklad pre study_program (ak ho pridáš do tabuľky):
      // {
      //   key: 'study_program',
      //   title: t('Študijný program'),
      //   dataIndex: 'study_program',
      //   ellipsis: true,
      //   sorter: dataGridTextSorter('study_program'),
      //   ...StudyProgramFilterProps,
      //   onFilter: (value, record) => {
      //     const v = String(value ?? '').toLowerCase();
      //     const sp = String(record?.study_program ?? '').toLowerCase();
      //     return sp.includes(v);
      //   },
      // },
    ],
    [
      t,
      LastNameFilterProps,
      FirstNameFilterProps,
      StatusFilterProps,
      createdDateRangeFilterProps,
      UniversityFilterProps,
      // StudyProgramFilterProps,
    ],
  );

  const selectedApplications = useMemo(() => {
    const selectedKeysSet = new Set(selectedRowKeys.map(String));
    return (filteredApplications || []).filter((application) =>
      selectedKeysSet.has(String(application.id)),
    );
  }, [filteredApplications, selectedRowKeys]);

  const assignableChallengeOptions = useMemo(
    () => (challengeOptions || []).filter((option) => option.value !== ''),
    [challengeOptions],
  );

  const hasBulkStateChanges = useMemo(
    () =>
      selectedApplications.some(
        (application) =>
          bulkStatusById[application.id] &&
          bulkStatusById[application.id] !== application.applicationStatus,
      ),
    [selectedApplications, bulkStatusById],
  );

  const hasBulkChallengeChanges = useMemo(
    () =>
      selectedApplications.some(
        (application) =>
          bulkChallengeById[application.id] &&
          bulkChallengeById[application.id] !== application.challenge_id,
      ),
    [selectedApplications, bulkChallengeById],
  );

  const selectedBulkExportOptionCount = useMemo(
    () => Object.values(bulkExportOptions).filter(Boolean).length,
    [bulkExportOptions],
  );

  if (!erasmusApplications) {
    return <Spinner />;
  }

  const rowSelection =
    hasErasmusElevatedAccess && selectionMode
      ? {
          checkStrictly: true,
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
        }
      : undefined;

  const renderExpandIcon = ({ expanded, onExpand, record, expandable }) => {
    if (!expandable) {
      return <span style={{ display: 'inline-block', width: 22 }} />;
    }

    const hasPendingUniversityChange =
      hasErasmusElevatedAccess && !!record?.requested_choosed_university_id;
    const expandIconClassName = [
      'ant-table-row-expand-icon',
      expanded
        ? 'ant-table-row-expand-icon-expanded'
        : 'ant-table-row-expand-icon-collapsed',
    ].join(' ');

    return (
      <Badge
        count={hasPendingUniversityChange ? 1 : 0}
        size="small"
        color="#ff4d4f"
        offset={[0, 2]}
      >
        <span
          className={expandIconClassName}
          style={{ display: 'inline-block' }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onExpand(record, event);
          }}
          aria-label={expanded ? t('Zbaliť riadok') : t('Rozbaliť riadok')}
          role="button"
        />
      </Badge>
    );
  };

  const handleOpenBulkChangeStateModal = () => {
    if (selectedApplications.length === 0) {
      return;
    }

    const initialMap = selectedApplications.reduce((acc, application) => {
      acc[application.id] = application.applicationStatus;
      return acc;
    }, {});

    setBulkStatusById(initialMap);
    setBulkStatusValue(null);
    setBulkChangeStateModalVisible(true);
  };

  const handleBulkStatusChange = (status) => {
    setBulkStatusValue(status);
    setBulkStatusById((prev) => {
      const next = { ...prev };
      selectedApplications.forEach((application) => {
        next[application.id] = status;
      });
      return next;
    });
  };

  const handleSingleBulkStatusChange = (applicationId, status) => {
    setBulkStatusById((prev) => ({
      ...prev,
      [applicationId]: status,
    }));
  };

  const handleOpenBulkChallengeModal = () => {
    if (selectedApplications.length === 0) {
      return;
    }

    const initialMap = selectedApplications.reduce((acc, application) => {
      acc[application.id] = application.challenge_id;
      return acc;
    }, {});

    setBulkChallengeById(initialMap);
    setBulkChallengeValue(null);
    setBulkChallengeModalVisible(true);
  };

  const handleBulkChallengeChange = (challengeId) => {
    setBulkChallengeValue(challengeId);
    setBulkChallengeById((prev) => {
      const next = { ...prev };
      selectedApplications.forEach((application) => {
        next[application.id] = challengeId;
      });
      return next;
    });
  };

  const handleSingleBulkChallengeChange = (applicationId, challengeId) => {
    setBulkChallengeById((prev) => ({
      ...prev,
      [applicationId]: challengeId,
    }));
  };

  const handleBulkStateSubmit = () => {
    if (!onChangeStateClick) {
      resetBulkStatusState();
      return;
    }

    selectedApplications.forEach((application) => {
      const nextStatus = bulkStatusById[application.id];
      if (!nextStatus || nextStatus === application.applicationStatus) {
        return;
      }
      onChangeStateClick(application, nextStatus);
    });

    resetBulkStatusState();
    setSelectedRowKeys([]);
  };

  const handleBulkChallengeSubmit = () => {
    if (!onChangeChallengeClick) {
      resetBulkChallengeState();
      return;
    }

    selectedApplications.forEach((application) => {
      const nextChallengeId = bulkChallengeById[application.id];
      if (!nextChallengeId || nextChallengeId === application.challenge_id) {
        return;
      }
      onChangeChallengeClick(application, nextChallengeId);
    });

    resetBulkChallengeState();
    setSelectedRowKeys([]);
  };

  const handleOpenBulkArchiveModal = () => {
    if (selectedApplications.length === 0) {
      return;
    }
    setBulkArchiveModalVisible(true);
  };

  const handleOpenBulkDeleteModal = () => {
    if (selectedApplications.length === 0) {
      return;
    }
    setBulkDeleteModalVisible(true);
  };

  const handleBulkArchiveConfirm = () => {
    if (onArchiveClick) {
      selectedApplications.forEach((application) => {
        onArchiveClick(application);
      });
    }

    setBulkArchiveModalVisible(false);
    setSelectedRowKeys([]);
  };

  const handleBulkDeleteConfirm = () => {
    if (onDelete) {
      selectedApplications.forEach((application) => {
        onDelete(application);
      });
    }

    setBulkDeleteModalVisible(false);
    setSelectedRowKeys([]);
  };

  const handleOpenBulkExportModal = () => {
    if (selectedApplications.length === 0) {
      return;
    }
    resetBulkExportProgress();
    resetBulkExportRuntime();
    setBulkExportModalVisible(true);
  };

  const updateBulkExportProgress = (next) => {
    setBulkExportProgress((prev) => ({
      ...prev,
      ...next,
    }));
  };

  const processBulkExportQueue = async () => {
    const runtime = bulkExportRuntimeRef.current;
    await runBulkExportQueue({
      runtime,
      t,
      executeTask: (task) =>
        executeBulkExportTask({
          task,
          downloadPrivateFile: triggerPrivateFileDownload,
          generateApplicationPDF,
          t,
        }),
      onBeforeStart: () => {
        setIsBulkExporting(true);
        setIsBulkExportPaused(false);
      },
      onProgress: updateBulkExportProgress,
      onPaused: (nextProgress) => {
        updateBulkExportProgress(nextProgress);
        setIsBulkExporting(false);
        setIsBulkExportPaused(true);
      },
      onCompleted: (nextProgress) => {
        updateBulkExportProgress(nextProgress);
        setSelectedRowKeys([]);
        setIsBulkExporting(false);
        setIsBulkExportPaused(false);
      },
      onEmpty: (nextProgress) => {
        updateBulkExportProgress(nextProgress);
        setIsBulkExporting(false);
        setIsBulkExportPaused(false);
      },
    });
  };

  const handleBulkExportConfirm = async () => {
    if (isBulkExporting || selectedApplications.length === 0) {
      return;
    }

    if (selectedBulkExportOptionCount === 0) {
      return;
    }

    const tasks = createBulkExportTasks({
      applications: selectedApplications,
      options: bulkExportOptions,
      t,
    });

    bulkExportRuntimeRef.current = {
      ...createBulkExportRuntime(),
      tasks,
    };

    updateBulkExportProgress({
      completed: 0,
      total: tasks.length,
      current: t('Pripravujem export...'),
      errors: [],
    });

    await processBulkExportQueue();
  };

  const handleBulkExportResume = async () => {
    if (isBulkExporting) {
      return;
    }
    await processBulkExportQueue();
  };

  const handleBulkExportResumeSkipAll = async () => {
    if (isBulkExporting) {
      return;
    }
    bulkExportRuntimeRef.current.skipAllErrors = true;
    await processBulkExportQueue();
  };

  return (
    <>
      <CollapseBlock collapsible={true} title={'Filter prihlášok'}>
        <Flex
          vertical={!screens.md}
          style={{
            alignItems: screens.md ? 'baseline' : 'stretch',
            justifyContent: 'space-between',
          }}
          gap="middle"
        >
          <Flex
            vertical={!screens.md}
            style={{ alignItems: screens.md ? 'baseline' : 'stretch' }}
            gap="middle"
          >
            <Radio.Group
              block
              value={selectedOption}
              optionType="button"
              onChange={handleRadioChange}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Radio value="active">{'Aktuálne'}</Radio>
              <Radio value="archived">{'Archivované'}</Radio>
            </Radio.Group>
            {challengeOptions && (
              <Flex style={{ alignItems: 'center' }} gap="small">
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  {'Výzva'}
                </span>
                <Select
                  placeholder="Vyberte výzvu"
                  value={selectedChallenge || ''}
                  onChange={setSelectedChallenge}
                  style={{
                    minWidth: screens.md ? '200px' : 0,
                    flex: screens.md ? undefined : 1,
                  }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={challengeOptions}
                />
              </Flex>
            )}
          </Flex>

          {hasErasmusElevatedAccess ? (
            !selectionMode ? (
              <div>
                <IconButton
                  icon={<AppstoreAddOutlined />}
                  tooltip={t('Hromadné akcie')}
                  onClick={toggleSelectionMode}
                />
              </div>
            ) : (
              <Space wrap>
                {selectedOption !== 'archived' && (
                  <IconButton
                    icon={<InteractionOutlined />}
                    tooltip={t('Zmena stavu')}
                    disabled={selectedRowKeys.length === 0}
                    disabledTooltip={t('Vyberte aspoň jednu prihlášku')}
                    onClick={handleOpenBulkChangeStateModal}
                  />
                )}
                {selectedOption !== 'archived' && (
                  <IconButton
                    icon={<SnippetsOutlined />}
                    tooltip={t('Nastaviť výzvu')}
                    disabled={selectedRowKeys.length === 0}
                    disabledTooltip={t('Vyberte aspoň jednu prihlášku')}
                    onClick={handleOpenBulkChallengeModal}
                  />
                )}
                {selectedOption !== 'archived' && (
                  <IconButton
                    icon={<InboxOutlined />}
                    tooltip={t('Archivovať')}
                    disabled={selectedRowKeys.length === 0}
                    disabledTooltip={t('Vyberte aspoň jednu prihlášku')}
                    onClick={handleOpenBulkArchiveModal}
                  />
                )}
                <IconButton
                  icon={<ExportOutlined />}
                  tooltip={t('Exportovať')}
                  disabled={selectedRowKeys.length === 0}
                  disabledTooltip={t('Vyberte aspoň jednu prihlášku')}
                  onClick={handleOpenBulkExportModal}
                />
                {selectedOption !== 'archived' && (
                  <IconButton
                    icon={<DeleteOutlined />}
                    tooltip={t('Zmazať')}
                    disabled={selectedRowKeys.length === 0}
                    disabledTooltip={t('Vyberte aspoň jednu prihlášku')}
                    onClick={handleOpenBulkDeleteModal}
                  />
                )}
                <IconButton
                  icon={<CloseOutlined />}
                  tooltip={t('Zatvoriť hromadné akcie')}
                  onClick={toggleSelectionMode}
                />
              </Space>
            )
          ) : (
            <div />
          )}
        </Flex>
      </CollapseBlock>

      <DataGrid
        uniqueTableId={uniqueTableId}
        rowKey={(application) => application.id}
        columns={columns}
        dataSource={filteredApplications || []}
        permissionsBasic={'erasmus-applications'}
        secondaryCondition={secondaryCondition}
        disableExpandRow={disableExpandRow}
        onEditClick={onDetailClick}
        onRowClick={
          selectionMode
            ? undefined
            : (row) => {
                if (onDetailClick && (secondaryCondition?.(row) ?? true)) {
                  onDetailClick(row);
                } else {
                  onPreviewClick?.(row);
                }
              }
        }
        onDelete={onDelete}
        onPreviewClick={onPreviewClick}
        rowClickOpensPreview={!selectionMode}
        rowSelection={rowSelection}
        customSettings={(row) => (
          <ApplicationTableSettings
            event={row}
            onSubmitClick={onSubmitClick}
            onCancelClick={onCancelClick}
            onChangeStateClick={onChangeStateClick}
            onPdfClick={onPdfClick}
            onTimelineClick={onTimelineClick}
            onArchiveClick={onArchiveClick}
            isAdmin={isAdmin}
            {...permsForRow}
            isArchived={!!row.archived_at}
            onDetailClick={onDetailClick}
            onEditClick={onDetailClick}
            onDelete={onDelete}
            onRequestSubmit={(r) => setRowToBeSubmitted(r)}
            onRequestCancel={(r) => setRowToBeCanceled(r)}
            onRequestArchive={(r) => setRowToBeArchived(r)}
            onRequestChangeState={(r, value) => {
              setRowToChangeState(r);
              setChangeStateValue(value);
            }}
            isPdfLoading={pdfGeneratingId === row.id}
          />
        )}
        customExpandedRowRender={customExpandedRowRender}
        expandedRowKeys={expandedRowKeys}
        onExpandedRowsChange={onExpandedRowsChange}
        expandIcon={customExpandedRowRender ? renderExpandIcon : undefined}
      />

      <QuestionModal
        chosenPositive={() => {
          if (rowToBeSubmitted) {
            onSubmitClick && onSubmitClick(rowToBeSubmitted);
            setRowToBeSubmitted(null);
          }
        }}
        question={t('Naozaj chcete odoslať túto prihlášku?')}
        visible={rowToBeSubmitted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeSubmitted(null)}
        headerTitle={t('Odoslať prihlášku')}
      />

      <QuestionModal
        chosenPositive={() => {
          if (rowToBeCanceled) {
            onCancelClick && onCancelClick(rowToBeCanceled);
            setRowToBeCanceled(null);
          }
        }}
        question={t('Naozaj chcete zrušiť túto prihlášku?')}
        visible={rowToBeCanceled !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeCanceled(null)}
        headerTitle={t('Zrušiť prihlášku')}
      />

      <QuestionModal
        chosenPositive={() => {
          if (rowToBeArchived) {
            onArchiveClick && onArchiveClick(rowToBeArchived);
            setRowToBeArchived(null);
          }
        }}
        question={t(
          'Naozaj chcete archivovať túto prihlášku? Prihláška bude presunutá do archívu.',
        )}
        visible={rowToBeArchived !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeArchived(null)}
        headerTitle={t('Archivovať prihlášku')}
      />

      <QuestionModal
        chosenPositive={(note) => {
          if (rowToChangeState && changeStateValue && onChangeStateClick) {
            onChangeStateClick(rowToChangeState, changeStateValue, note);
          }
          setRowToChangeState(null);
          setChangeStateValue(null);
        }}
        question={t(
          `Naozaj chcete zmeniť status tejto prihlášky na ${changeStateValue} ?`,
        )}
        visible={changeStateValue !== null}
        yes={t('Áno')}
        toggle={() => {
          setChangeStateValue(null);
          setRowToChangeState(null);
        }}
        headerTitle={t('Zmeniť status prihlášky')}
        isNote={true}
      />

      <Modal
        open={bulkChangeStateModalVisible}
        title={t('Hromadná zmena stavu')}
        onCancel={resetBulkStatusState}
        onOk={handleBulkStateSubmit}
        okText={t('Uložiť')}
        cancelText={t('Zrušiť')}
        okButtonProps={{ disabled: !hasBulkStateChanges }}
        width={920}
      >
        <Flex vertical gap="middle">
          <Flex
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
            gap="middle"
          >
            <span style={{ fontWeight: 600 }}>
              {t('Nastaviť všetkým rovnaký stav')}
            </span>
            <Select
              placeholder={t('Vyberte stav')}
              value={bulkStatusValue}
              onChange={handleBulkStatusChange}
              style={{ minWidth: 260 }}
              options={APPLICATION_STATUS_OPTIONS.map((statusOption) => ({
                value: statusOption.value,
                label: (
                  <Tag
                    color={getApplicationStatusColor(statusOption.value)}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {statusOption.label.toUpperCase()}
                  </Tag>
                ),
              }))}
            />
          </Flex>

          <Divider style={{ margin: '4px 0 8px' }} />

          <div style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
            <Flex vertical gap="small">
              {selectedApplications.map((application) => (
                <Flex
                  key={application.id}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '8px 10px',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                  }}
                >
                  <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>
                      {getPersonDisplayName(application)}
                    </span>
                    <Flex
                      style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      <span>{t('Aktuálny stav')}:</span>
                      <Tag
                        color={getApplicationStatusColor(
                          application.applicationStatus,
                        )}
                      >
                        {String(
                          application.applicationStatus || '',
                        ).toUpperCase()}
                      </Tag>
                    </Flex>
                  </Flex>

                  <Select
                    value={bulkStatusById[application.id]}
                    onChange={(value) =>
                      handleSingleBulkStatusChange(application.id, value)
                    }
                    style={{ minWidth: 220 }}
                    options={APPLICATION_STATUS_OPTIONS.map((statusOption) => ({
                      value: statusOption.value,
                      label: (
                        <Tag
                          color={getApplicationStatusColor(statusOption.value)}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {statusOption.label.toUpperCase()}
                        </Tag>
                      ),
                    }))}
                  />
                </Flex>
              ))}
            </Flex>
          </div>
        </Flex>
      </Modal>

      <Modal
        open={bulkChallengeModalVisible}
        title={t('Hromadné nastavenie výzvy')}
        onCancel={resetBulkChallengeState}
        onOk={handleBulkChallengeSubmit}
        okText={t('Uložiť')}
        cancelText={t('Zrušiť')}
        okButtonProps={{ disabled: !hasBulkChallengeChanges }}
        width={980}
      >
        <Flex vertical gap="middle">
          <Flex
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
            gap="middle"
          >
            <span style={{ fontWeight: 600 }}>
              {t('Nastaviť všetkým rovnakú výzvu')}
            </span>
            <Select
              placeholder={t('Vyberte výzvu')}
              value={bulkChallengeValue}
              onChange={handleBulkChallengeChange}
              style={{ minWidth: 360 }}
              options={assignableChallengeOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Flex>

          <Divider style={{ margin: '4px 0 8px' }} />

          <div style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
            <Flex vertical gap="small">
              {selectedApplications.map((application) => (
                <Flex
                  key={application.id}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '8px 10px',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                  }}
                >
                  <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>
                      {getPersonDisplayName(application)}
                    </span>
                    <span style={{ color: '#595959' }}>
                      {application?.choosed_university?.name ||
                        t('Neznáma univerzita')}
                    </span>
                  </Flex>

                  <Select
                    value={bulkChallengeById[application.id]}
                    onChange={(value) =>
                      handleSingleBulkChallengeChange(application.id, value)
                    }
                    style={{ minWidth: 360 }}
                    options={assignableChallengeOptions}
                    showSearch
                    filterOption={(input, option) => {
                      if (!option?.label) return false;
                      return String(option.label)
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                  />
                </Flex>
              ))}
            </Flex>
          </div>
        </Flex>
      </Modal>

      <Modal
        open={bulkExportModalVisible}
        title={t('Hromadný export prihlášok')}
        onCancel={() => {
          if (!isBulkExporting) {
            setBulkExportModalVisible(false);
            resetBulkExportRuntime();
            resetBulkExportProgress();
          }
        }}
        onOk={handleBulkExportConfirm}
        okText={isBulkExporting ? t('Exportujem...') : t('Exportovať')}
        cancelText={t('Zrušiť')}
        okButtonProps={{
          disabled:
            isBulkExporting ||
            selectedApplications.length === 0 ||
            selectedBulkExportOptionCount === 0,
          loading: isBulkExporting,
        }}
        cancelButtonProps={{ disabled: isBulkExporting }}
        width={980}
      >
        <Flex vertical gap="middle">
          <Flex vertical gap="small">
            <span style={{ fontWeight: 600 }}>
              {t('Vyberte obsah exportu')}
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
              }}
            >
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: '#fafafa',
                }}
              >
                <Checkbox
                  checked={bulkExportOptions.includeCv}
                  disabled={isBulkExporting}
                  onChange={(e) =>
                    setBulkExportOptions((prev) => ({
                      ...prev,
                      includeCv: e.target.checked,
                    }))
                  }
                >
                  {t('Životopis')}
                </Checkbox>
              </div>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: '#fafafa',
                }}
              >
                <Checkbox
                  checked={bulkExportOptions.includeMotivationLetter}
                  disabled={isBulkExporting}
                  onChange={(e) =>
                    setBulkExportOptions((prev) => ({
                      ...prev,
                      includeMotivationLetter: e.target.checked,
                    }))
                  }
                >
                  {t('Motivačný list')}
                </Checkbox>
              </div>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: '#fafafa',
                }}
              >
                <Checkbox
                  checked={bulkExportOptions.includePhoto}
                  disabled={isBulkExporting}
                  onChange={(e) =>
                    setBulkExportOptions((prev) => ({
                      ...prev,
                      includePhoto: e.target.checked,
                    }))
                  }
                >
                  {t('Fotografia')}
                </Checkbox>
              </div>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: '#fafafa',
                }}
              >
                <Checkbox
                  checked={bulkExportOptions.includeLanguageCertificate}
                  disabled={isBulkExporting}
                  onChange={(e) =>
                    setBulkExportOptions((prev) => ({
                      ...prev,
                      includeLanguageCertificate: e.target.checked,
                    }))
                  }
                >
                  {t('Jazykový certifikát')}
                </Checkbox>
              </div>
              <div
                style={{
                  border: '1px solid #ffa940',
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: '#fff7e6',
                }}
              >
                <Checkbox
                  checked={bulkExportOptions.includePdf}
                  disabled={isBulkExporting}
                  onChange={(e) =>
                    setBulkExportOptions((prev) => ({
                      ...prev,
                      includePdf: e.target.checked,
                    }))
                  }
                >
                  <span style={{ color: '#d46b08', fontWeight: 600 }}>
                    {t('Vygenerované PDF')}
                  </span>
                </Checkbox>
              </div>
            </div>
          </Flex>

          <Divider style={{ margin: '4px 0 8px' }} />

          <Flex vertical gap="small">
            <span style={{ fontWeight: 600 }}>{t('Vybrané prihlášky')}</span>
            <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
              {selectedApplications.map((application) => (
                <div
                  key={`export-${application.id}`}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '8px 10px',
                    marginBottom: 8,
                  }}
                >
                  <strong>{getPersonDisplayName(application)}</strong>
                  {' - '}
                  {application?.choosed_university?.name ||
                    'Neznáma univerzita'}
                </div>
              ))}
            </div>
          </Flex>

          <Divider style={{ margin: '4px 0 8px' }} />

          <Flex vertical gap="small">
            <span style={{ fontWeight: 600 }}>{t('Priebeh exportu')}</span>
            <Progress
              percent={
                bulkExportProgress.total > 0
                  ? Math.round(
                      (bulkExportProgress.completed /
                        bulkExportProgress.total) *
                        100,
                    )
                  : 0
              }
              status={
                isBulkExporting
                  ? 'active'
                  : bulkExportProgress.current === t('Export dokončený')
                    ? 'success'
                    : 'normal'
              }
            />
            <span style={{ color: '#595959' }}>
              {bulkExportProgress.current || t('Čaká na spustenie')}
            </span>
            {bulkExportProgress.errors.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={t('Niektoré súbory sa nepodarilo exportovať')}
                description={
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {bulkExportProgress.errors.map((errorLine, index) => (
                      <div key={`export-error-${index}`}>{errorLine}</div>
                    ))}
                  </div>
                }
              />
            )}
            {isBulkExportPaused && !isBulkExporting && (
              <div
                style={{
                  border: '1px solid #ffd591',
                  background: '#fff7e6',
                  borderRadius: 10,
                  padding: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span style={{ color: '#ad4e00', fontWeight: 600 }}>
                  {t('Export bol pozastavený na chybe')}
                </span>
                <Space.Compact
                  style={{
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                >
                  <Button
                    type="primary"
                    icon={<CaretRightOutlined />}
                    onClick={handleBulkExportResume}
                  >
                    {t('Pokračovať')}
                  </Button>
                  <Dropdown
                    placement="bottomRight"
                    menu={{
                      items: [
                        {
                          key: 'skip-all-errors',
                          icon: <FastForwardOutlined />,
                          label: t('Pokračovať a preskakovať chybné súbory'),
                        },
                      ],
                      onClick: ({ key }) => {
                        if (key === 'skip-all-errors') {
                          handleBulkExportResumeSkipAll();
                        }
                      },
                    }}
                  >
                    <Button type="primary" icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space.Compact>
              </div>
            )}
          </Flex>
        </Flex>
      </Modal>

      <QuestionModal
        chosenPositive={handleBulkArchiveConfirm}
        question={
          <div>
            <p style={{ marginBottom: 8 }}>
              {t('Naozaj chcete archivovať vybrané prihlášky?')}
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {selectedApplications.map((application) => (
                <div key={application.id}>
                  <strong>{getPersonDisplayName(application)}</strong>
                  {' - '}
                  {application?.choosed_university?.name ||
                    'Neznáma univerzita'}
                </div>
              ))}
            </div>
          </div>
        }
        visible={bulkArchiveModalVisible}
        yes={t('Archivovať')}
        toggle={() => setBulkArchiveModalVisible(false)}
        headerTitle={t('Archivovať prihlášky')}
      />

      <QuestionModal
        chosenPositive={handleBulkDeleteConfirm}
        question={
          <div>
            <p style={{ marginBottom: 8 }}>
              {t('Naozaj chcete zmazať vybrané prihlášky?')}
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {selectedApplications.map((application) => (
                <div key={application.id}>
                  <strong>{getPersonDisplayName(application)}</strong>
                  {' - '}
                  {application?.choosed_university?.name ||
                    'Neznáma univerzita'}
                </div>
              ))}
            </div>
          </div>
        }
        visible={bulkDeleteModalVisible}
        yes={t('Zmazať')}
        toggle={() => setBulkDeleteModalVisible(false)}
        headerTitle={t('Zmazať prihlášky')}
      />
    </>
  );
};

ErasmusApplicationsTable.propTypes = {
  erasmusApplications: PropTypes.array,
  onDelete: PropTypes.func,
  disableExpandRow: PropTypes.func,
  onDetailClick: PropTypes.func,
  onCreateClick: PropTypes.func,
  onPreviewClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  onSubmitClick: PropTypes.func,
  onPdfClick: PropTypes.func,
  onTimelineClick: PropTypes.func,
  onArchiveClick: PropTypes.func,
  onChangeChallengeClick: PropTypes.func,
  customExpandedRowRender: PropTypes.func,
  uniqueTableId: PropTypes.string,
  secondaryCondition: PropTypes.func,
  selectedOption: PropTypes.string,
  setSelectedOption: PropTypes.func,
  selectedChallenge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedChallenge: PropTypes.func,
  challengeOptions: PropTypes.array,
  pdfGeneratingId: PropTypes.number,
};

export default ErasmusApplicationsTable;
